import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../services/authService';
import { useBranding, clearBrandingCache } from './BrandingContext';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const branding = useBranding();
  // Consider auth possible if we have an access token OR a refresh token
  const [authenticated, setAuthenticated] = useState(authService.isAuthenticated());
  const [authReady, setAuthReady] = useState(authService.isAuthenticated());
  const [user, setUser] = useState(null);

  // On mount: if no access token but refresh token exists, try silent refresh
  useEffect(() => {
    if (!authService.isAuthenticated() && authService.hasRefreshToken()) {
      authService.refresh()
        .then(() => {
          setAuthenticated(true);
          setAuthReady(true);
        })
        .catch(() => {
          setAuthenticated(false);
          setAuthReady(true);
        });
    } else {
      setAuthReady(true);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await authService.login(email, password);
    if (!result.mfaRequired) {
      setAuthenticated(true);
    }
    return result;
  }, []);

  const verifyMfa = useCallback(async (email, code) => {
    const result = await authService.verifyMfa(email, code);
    setAuthenticated(true);
    return result;
  }, []);

  const completeSso = useCallback(async (code) => {
    const result = await authService.completeSso(code);
    setAuthenticated(true);
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    clearBrandingCache();
    setAuthenticated(false);
    setUser(null);
    window.location.href = '/login';
  }, []);

  const getAccessToken = useCallback(async () => {
    // If access token is missing but refresh token exists, attempt silent refresh
    if (!authService.isAuthenticated()) {
      if (authService.hasRefreshToken()) {
        try {
          const newToken = await authService.refresh();
          setAuthenticated(true);
          return newToken;
        } catch {
          setAuthenticated(false);
          throw new Error('Session expired');
        }
      }
      throw new Error('Not authenticated');
    }
    if (authService.isTokenExpiringSoon()) {
      try {
        return await authService.refresh();
      } catch {
        setAuthenticated(false);
        throw new Error('Session expired');
      }
    }
    return authService.getAccessToken();
  }, []);

  // Allow UserContext or other components to set user info
  const setUserInfo = useCallback((profile) => {
    if (!profile) { setUser(null); return; }
    const name = profile.name || profile.displayName || profile.email || 'User';
    setUser({
      memberId: profile.memberId || profile.id || null,
      name,
      initials: name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase(),
      email: profile.email || '',
      mfaEnabled: profile.totpEnabled ?? false,
      role: profile.role || profile.callerRole || 'User',
      org: branding?.portalName || 'Sentinel Command',
    });
  }, [branding]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: authenticated,
      authReady,
      login,
      verifyMfa,
      completeSso,
      logout,
      getAccessToken,
      setUserInfo,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function usePlatformAdmin() {
  const { user } = useAuth();
  // Platform admin is determined by backend profile, not email domain
  return user?.role === 'PlatformAdmin';
}
